/**
 * BulkSnapshotPreview - Read-only view of a bulk registration snapshot.
 * Renders identically to AdminBulkView's table layout.
 */
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

function renderMd(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <h4 key={i} className="font-semibold text-slate-900 mt-3 mb-1">{boldify(line.slice(4))}</h4>;
    if (line.startsWith('## ')) return <h3 key={i} className="font-bold text-slate-900 mt-3 mb-1.5">{boldify(line.slice(3))}</h3>;
    if (line.startsWith('- ')) return <div key={i} className="flex gap-2 ml-1 my-0.5"><span className="text-teal-500">•</span><span>{boldify(line.slice(2))}</span></div>;
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} className="my-0.5">{boldify(line)}</p>;
  });
}
function boldify(t) {
  const parts = []; let r = t, k = 0;
  while (r) { const m = r.match(/\*\*(.+?)\*\*/); if (m) { const i = r.indexOf(m[0]); if (i > 0) parts.push(<span key={k++}>{r.slice(0, i)}</span>); parts.push(<strong key={k++}>{m[1]}</strong>); r = r.slice(i + m[0].length); } else { parts.push(<span key={k++}>{r}</span>); break; } }
  return parts;
}

const COLOR_MAP = {
  green: 'bg-emerald-100',
  yellow: 'bg-yellow-100',
  orange: 'bg-orange-100',
  red: 'bg-red-100',
};

function getShortName(fullName) {
  const m = fullName.match(/- (.+)$/);
  return m ? m[1] : fullName;
}

function getLetterFromLoc(loc) {
  return loc.split(' - ')[0]?.trim() || loc.slice(0, 1);
}

function formatValue(val) {
  if (!val || val === '' || val === '0') return '–';
  const num = parseInt(String(val).replace(/[<>\s]/g, ''), 10);
  if (String(val).startsWith('<')) return `<${num.toLocaleString('nb-NO')}`;
  if (!isNaN(num)) return num.toLocaleString('nb-NO');
  return val;
}

export default function BulkSnapshotPreview({ snapshot, versionLabel }) {
  if (!snapshot) return null;

  const algaeData = snapshot.algae_data || {};
  const zooData = snapshot.zooplankton_data || {};
  const colorOverrides = snapshot.color_overrides || {};
  const tempData = snapshot.temperature_data || {};

  // Get locations from first organism
  const firstOrg = Object.values(algaeData)[0] || Object.values(zooData)[0] || {};
  const locations = Object.keys(firstOrg);

  const renderTable = (data, title, unit) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <Card className="border border-slate-200 shadow-sm mb-6">
        <CardHeader className="py-3 px-4 border-b border-slate-100">
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b-2 border-slate-300">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-50 min-w-[130px]">Orden/gruppe</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider sticky bg-slate-50 min-w-[130px]" style={{left: '130px'}}>Evt. art</th>
                  {locations.map(loc => (
                    <th key={loc} className="px-2 py-2 text-center text-xs font-semibold text-slate-600 bg-slate-50 min-w-[75px]">
                      <div>{getShortName(loc)}</div>
                      <div className="text-[10px] text-slate-400 font-normal">({unit})</div>
                      {tempData[loc] && (
                        <div className="mt-0.5 text-[10px] font-semibold text-sky-600 bg-sky-50 rounded-full px-1.5 py-0.5 inline-block">
                          {tempData[loc]}°C
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(data).map(([orgKey, locData], i) => {
                  const parts = orgKey.split('_');
                  const group = parts[0] || '';
                  const species = parts.slice(1).join('_') || orgKey;
                  const isSUM = species.toUpperCase().includes('SUM');

                  return (
                    <tr key={orgKey} className={`border-b border-slate-100 ${isSUM ? 'bg-slate-100 font-semibold' : ''}`}>
                      <td className={`px-3 py-2 text-slate-700 sticky left-0 ${isSUM ? 'bg-slate-100' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                        {group}
                      </td>
                      <td className={`px-3 py-2 text-slate-800 italic sticky ${isSUM ? 'bg-slate-100 not-italic font-bold' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} style={{left: '130px'}}>
                        {species}
                      </td>
                      {locations.map(loc => {
                        const val = locData?.[loc] || '';
                        const overrideKey = `${orgKey}_${loc}`;
                        const colorClass = colorOverrides[overrideKey] ? COLOR_MAP[colorOverrides[overrideKey]] || '' : '';
                        return (
                          <td key={loc} className={`px-2 py-2 text-right tabular-nums ${isSUM ? 'font-bold' : ''} ${colorClass || (isSUM ? 'bg-teal-50' : '')}`}>
                            {formatValue(val)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      {/* Header info */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5 text-sm text-slate-600">
        {snapshot.date && <span>Dato: <strong className="text-slate-900">{snapshot.date}</strong></span>}
        {snapshot.area_name && <span>Område: <strong className="text-slate-900">{snapshot.area_name}</strong></span>}
        {snapshot.biologist && <span>Biolog: <strong className="text-slate-900">{snapshot.biologist}</strong></span>}
        {snapshot.sample_count && <span>Stasjonar: <strong className="text-slate-900">{snapshot.sample_count}</strong></span>}
      </div>

      {/* Summary */}
      {snapshot.ai_summary && (
        <Card className="border border-slate-200 shadow-sm mb-6">
          <CardHeader className="py-3 px-4 border-b border-slate-100">
            <CardTitle className="text-base">Laboratorie sammendrag</CardTitle>
          </CardHeader>
          <CardContent className="p-4 text-sm leading-relaxed text-slate-700">
            {renderMd(snapshot.ai_summary)}
          </CardContent>
        </Card>
      )}

      {/* Tables */}
      {renderTable(algaeData, 'Algeresultater', 'ant/l')}
      {renderTable(zooData, 'Zooplankton resultater', 'ant/m³')}
    </div>
  );
}
